/**
 * Live, editable document: DocSync over the HTTP op log + Server-Sent Events.
 * Local edits show at once and queue (persisted in localStorage, so a closed
 * tab or a dropped connection loses nothing); other people's edits stream in.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  DocSync, httpTransport, newItemId, type CenterlineDocument, type LogEntry, type Operation, type PendingOp, type SyncStatus,
} from "@centerline/core/viewer";
import type { DocumentSummary } from "./api";

export interface Notice { id: number; kind: "info" | "warn"; text: string }

const CLIENT_KEY = "centerline.clientId";
const ACTOR_KEY = "centerline.actor";

export function clientId(): string {
  // Per tab: two tabs are two editing sessions (sessionStorage survives reload, not new tabs).
  let id = sessionStorage.getItem(CLIENT_KEY);
  if (!id) { id = `web-${newItemId().slice(0, 13)}`; sessionStorage.setItem(CLIENT_KEY, id); }
  return id;
}
export function actorName(): string { return localStorage.getItem(ACTOR_KEY) || "Web user"; }
export function setActorName(name: string): void { localStorage.setItem(ACTOR_KEY, name.trim().slice(0, 60)); }

const OP_LABEL: Partial<Record<Operation["type"], string>> = {
  addMarkup: "added a markup", deleteMarkup: "deleted a markup", patchMarkup: "edited a markup", moveMarkup: "moved a markup",
  addMeasurement: "added a takeoff", deleteMeasurement: "deleted a takeoff", patchMeasurement: "edited a takeoff", moveMeasurement: "moved a takeoff",
  setPageScale: "set a sheet scale", setSheetLabel: "renamed a sheet", upsertStampDefinition: "saved a stamp design", deleteStampDefinition: "removed a stamp design",
  setLayerVisibility: "changed layer visibility", renameDocument: "renamed the plan set", batch: "made changes", addSheet: "added a sheet", removeSheet: "removed a sheet",
};

export function useDocSync(id: string) {
  const [meta, setMeta] = useState<DocumentSummary | null>(null);
  const [doc, setDoc] = useState<CenterlineDocument | null>(null);
  const [status, setStatus] = useState<SyncStatus>("synced");
  const [pending, setPending] = useState(0);
  const [seq, setSeq] = useState(0);
  const [live, setLive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notices, setNotices] = useState<Notice[]>([]);
  const [history, setHistory] = useState({ canUndo: false, canRedo: false });
  const syncRef = useRef<DocSync | null>(null);
  const noticeId = useRef(0);

  const notify = useCallback((kind: Notice["kind"], text: string) => {
    const n = { id: ++noticeId.current, kind, text };
    setNotices((list) => [...list.slice(-3), n]);
    setTimeout(() => setNotices((list) => list.filter((x) => x.id !== n.id)), kind === "warn" ? 9000 : 4500);
  }, []);

  useEffect(() => {
    let closed = false;
    let es: EventSource | null = null;
    const queueKey = `centerline.queue.${id}`;
    const transport = httpTransport({ baseUrl: "", documentId: id });
    const refresh = () => {
      const s = syncRef.current;
      if (!s) return;
      setDoc(s.view); setSeq(s.confirmedSeq); setPending(s.pending.length);
      setHistory({ canUndo: s.canUndo, canRedo: s.canRedo });
    };
    (async () => {
      const r = await fetch(`/api/documents/${id}`, { credentials: "same-origin" });
      if (r.status === 401) { window.dispatchEvent(new Event("cl:unauthorized")); return; }
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message ?? `Could not open the document (${r.status})`);
      const body = await r.json() as { meta: DocumentSummary & { headSeq?: number }; document: CenterlineDocument };
      if (closed) return;
      let restored: PendingOp[] = [];
      try { restored = JSON.parse(localStorage.getItem(queueKey) ?? "[]"); } catch { /* corrupt queue */ }
      const me = clientId();
      const sync = new DocSync(body.document, body.meta.headSeq ?? 0, {
        clientId: me, actor: actorName(), transport, newOpId: () => newItemId().toLowerCase(), pending: restored, flushDelayMs: 30,
        onPendingChange: (p) => { if (p.length) localStorage.setItem(queueKey, JSON.stringify(p)); else localStorage.removeItem(queueKey); },
        onEvent: (e) => {
          if (e.type === "status") setStatus(e.status);
          else if (e.type === "rejected") notify("warn", e.code === "stale_base" || e.code === "conflict"
            ? `Your change didn't go through: ${e.message}`
            : `Change refused by the server (${e.code}): ${e.message}`);
          else if (e.type === "overwrote") notify("info", "Your edit replaced a change someone else made a moment ago (last edit wins).");
          else if (e.type === "remote") {
            const byActor = new Map<string, LogEntry[]>();
            for (const x of e.entries) byActor.set(x.actor, [...(byActor.get(x.actor) ?? []), x]);
            for (const [who, list] of byActor) notify("info", `${who} ${list.length === 1 ? OP_LABEL[list[0]!.op.type] ?? "made a change" : `made ${list.length} changes`}`);
          } else if (e.type === "reset") notify("info", "Reloaded the latest version from the server.");
          refresh();
        },
      });
      syncRef.current = sync;
      if (restored.length) notify("info", `Sending ${restored.length} change${restored.length === 1 ? "" : "s"} saved while offline…`);
      setMeta(body.meta);
      refresh();
      es = new EventSource(`/api/documents/${id}/events?since=${sync.confirmedSeq}`);
      es.addEventListener("hello", () => setLive(true));
      es.addEventListener("op", (ev) => sync.receive([JSON.parse((ev as MessageEvent).data)]));
      es.addEventListener("reset", () => void sync.resync().catch(() => undefined));
      es.onerror = () => setLive(false);
      es.onopen = () => { setLive(true); void sync.catchUp(); };
    })().catch((e) => !closed && setError((e as Error).message));
    const online = () => void syncRef.current?.retryNow();
    window.addEventListener("online", online);
    return () => {
      closed = true;
      es?.close();
      syncRef.current?.close();
      syncRef.current = null;
      window.removeEventListener("online", online);
    };
  }, [id, notify]);

  /** Applies a local edit; returns false (and says why) if the core refuses it. */
  const apply = useCallback((op: Operation): boolean => {
    const s = syncRef.current;
    if (!s) return false;
    try { s.apply(op); return true; } catch (err) { notify("warn", (err as Error).message); return false; }
  }, [notify]);
  const undo = useCallback(() => { if (!syncRef.current?.undo()) notify("info", "Nothing to undo."); }, [notify]);
  const redo = useCallback(() => { if (!syncRef.current?.redo()) notify("info", "Nothing to redo."); }, [notify]);

  return { meta, doc, status, pending, seq, live, error, notices, history, apply, undo, redo, notify };
}
